import type { CatalogJobDetail, CatalogJobListItem } from '@tabbio/types';
import type { CVProfile, CVSkillGroup } from './cv-mock-data';

export type CanonicalRoleFamilyKey = string;

export interface CanonicalRoleFamily {
  key: CanonicalRoleFamilyKey;
  label: string;
  labelAr: string;
  technical: boolean;
  roles: string[];
  skills: string[];
  aliases: string[];
}

export interface CanonicalTaxonomyOption {
  id: string;
  key: string;
  familyKey: CanonicalRoleFamilyKey;
  familyLabel: string;
  familyLabelAr: string;
  label: string;
  labelAr: string;
  aliases: string[];
  kind: 'skill' | 'role';
}

export interface ProfileTaxonomySignals {
  familyKeys: CanonicalRoleFamilyKey[];
  primaryFamily: CanonicalRoleFamily | null;
  skillHints: string[];
  roleHints: string[];
  query: string;
}

export interface CatalogJobFitSummary {
  roleFamily: string | null;
  roleKey: string | null;
  roleConfidence: number | null;
  matchedSkills: string[];
  missingSkills: string[];
  contentCompleteness: number | null;
  qualityFlags: string[];
  surfaceEligible: boolean;
}

type CatalogJobTaxonomyInput = CatalogJobListItem | CatalogJobDetail;

const TECH_FAMILIES = new Set<CanonicalRoleFamilyKey>([
  'frontend',
  'backend',
  'fullstack',
  'mobile',
  'data',
  'ai',
  'infrastructure',
  'devops',
  'qa',
  'security',
]);

const FAMILY_SKILL_LIMIT = 6;

export const CANONICAL_ROLE_FAMILIES: CanonicalRoleFamily[] = [
  {
    key: 'frontend',
    label: 'Frontend',
    labelAr: 'الواجهة الأمامية',
    technical: true,
    roles: [
      'Frontend Engineer',
      'React Developer',
      'React Native Engineer',
      'UI Engineer',
      'Web Engineer',
    ],
    skills: [
      'React',
      'React Native',
      'TypeScript',
      'Next.js',
      'JavaScript',
      'HTML',
      'CSS',
      'Tailwind CSS',
      'Redux',
      'GraphQL',
      'Jest',
      'Expo',
      'Accessibility',
    ],
    aliases: ['frontend', 'front end', 'ui engineer', 'react', 'react native', 'web engineer'],
  },
  {
    key: 'backend',
    label: 'Backend',
    labelAr: 'الخلفية',
    technical: true,
    roles: ['Backend Engineer', 'API Engineer', 'Platform Engineer', 'Node.js Engineer'],
    skills: ['Node.js', 'NestJS', 'Express', 'PostgreSQL', 'Prisma', 'Redis', 'Kafka', 'Docker', 'REST APIs', 'Microservices'],
    aliases: ['backend', 'back end', 'api engineer', 'node', 'platform engineer'],
  },
  {
    key: 'fullstack',
    label: 'Full-stack',
    labelAr: 'متكامل',
    technical: true,
    roles: ['Full-stack Engineer', 'Full Stack Developer', 'Product Engineer'],
    skills: ['React', 'Node.js', 'TypeScript', 'GraphQL', 'PostgreSQL', 'Next.js', 'NestJS', 'REST APIs', 'Docker'],
    aliases: ['fullstack', 'full stack', 'product engineer'],
  },
  {
    key: 'mobile',
    label: 'Mobile',
    labelAr: 'الجوال',
    technical: true,
    roles: ['Mobile Engineer', 'React Native Developer', 'iOS Engineer', 'Android Engineer'],
    skills: ['React Native', 'Expo', 'Swift', 'Kotlin', 'Android', 'iOS', 'Flutter'],
    aliases: ['mobile', 'react native', 'ios engineer', 'android engineer', 'expo'],
  },
  {
    key: 'data',
    label: 'Data',
    labelAr: 'البيانات',
    technical: true,
    roles: ['Data Analyst', 'Data Scientist', 'Analytics Engineer', 'BI Analyst'],
    skills: ['SQL', 'Python', 'Excel', 'Power BI', 'Tableau', 'dbt', 'Looker', 'Snowflake', 'BigQuery'],
    aliases: ['data', 'analytics', 'bi analyst', 'data scientist', 'data analyst'],
  },
  {
    key: 'ai',
    label: 'AI',
    labelAr: 'الذكاء الاصطناعي',
    technical: true,
    roles: ['AI Engineer', 'ML Engineer', 'GenAI Engineer', 'Applied Scientist'],
    skills: ['Python', 'LLMs', 'Machine Learning', 'Prompt Engineering', 'RAG', 'PyTorch', 'TensorFlow', 'OpenAI'],
    aliases: ['ai', 'ml', 'machine learning', 'genai', 'llm', 'prompt engineering'],
  },
  {
    key: 'infrastructure',
    label: 'Infrastructure / IT',
    labelAr: 'البنية التحتية وتقنية المعلومات',
    technical: true,
    roles: ['IT Support Engineer', 'Cloud Engineer', 'Network Engineer', 'Systems Administrator'],
    skills: ['AWS', 'Azure', 'Linux', 'Windows Server', 'Networking', 'Active Directory', 'Docker', 'Kubernetes'],
    aliases: ['it', 'infrastructure', 'cloud engineer', 'network engineer', 'systems administrator'],
  },
  {
    key: 'devops',
    label: 'DevOps',
    labelAr: 'ديف أوبس',
    technical: true,
    roles: ['DevOps Engineer', 'Cloud Engineer', 'SRE', 'Infrastructure Engineer'],
    skills: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD', 'Linux', 'Monitoring', 'GitHub Actions'],
    aliases: ['devops', 'cloud engineer', 'sre', 'infrastructure engineer'],
  },
  {
    key: 'qa',
    label: 'QA',
    labelAr: 'ضمان الجودة',
    technical: true,
    roles: ['QA Engineer', 'Test Automation Engineer', 'SDET', 'Quality Engineer'],
    skills: ['Test Automation', 'Jest', 'Playwright', 'Cypress', 'Appium', 'Manual Testing', 'Regression Testing'],
    aliases: ['qa', 'quality assurance', 'test automation', 'sdet', 'quality engineer'],
  },
  {
    key: 'security',
    label: 'Security',
    labelAr: 'الأمن',
    technical: true,
    roles: ['Security Engineer', 'Application Security Engineer', 'SOC Analyst', 'Security Analyst'],
    skills: ['Threat Modeling', 'IAM', 'Vulnerability Management', 'Pen Testing', 'SIEM', 'Cloud Security'],
    aliases: ['security', 'infosec', 'application security', 'soc analyst', 'cyber'],
  },
  {
    key: 'product',
    label: 'Product',
    labelAr: 'المنتج',
    technical: false,
    roles: ['Product Manager', 'Product Owner', 'Growth Product Manager', 'Platform Product Manager'],
    skills: ['Roadmapping', 'Prioritization', 'Analytics', 'Discovery', 'Stakeholder Management', 'A/B Testing'],
    aliases: ['product', 'product manager', 'product owner', 'roadmap', 'growth product'],
  },
  {
    key: 'design',
    label: 'Design',
    labelAr: 'التصميم',
    technical: false,
    roles: ['Product Designer', 'UI Designer', 'UX Researcher', 'Visual Designer'],
    skills: ['Figma', 'User Research', 'Interaction Design', 'Design Systems', 'Prototyping', 'Wireframing'],
    aliases: ['design', 'ui designer', 'ux', 'product designer', 'figma'],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    labelAr: 'التسويق',
    technical: false,
    roles: ['Growth Marketer', 'Performance Marketer', 'Content Marketer', 'Digital Marketing Manager'],
    skills: ['SEO', 'Paid Ads', 'Content Strategy', 'CRM', 'Lifecycle Marketing', 'Analytics'],
    aliases: ['marketing', 'growth marketer', 'seo', 'performance marketing', 'content marketing'],
  },
  {
    key: 'sales',
    label: 'Sales',
    labelAr: 'المبيعات',
    technical: false,
    roles: ['Account Executive', 'Sales Manager', 'Business Development Manager', 'Partnerships Manager'],
    skills: ['Pipeline Management', 'Negotiation', 'Lead Generation', 'CRM', 'Forecasting', 'Closing'],
    aliases: ['sales', 'account executive', 'business development', 'partnerships manager'],
  },
  {
    key: 'operations',
    label: 'Operations',
    labelAr: 'العمليات',
    technical: false,
    roles: ['Operations Manager', 'Program Manager', 'Project Manager', 'Business Operations Manager'],
    skills: ['Process Improvement', 'Planning', 'Execution', 'Workflow Design', 'Reporting', 'OKRs'],
    aliases: ['operations', 'project manager', 'program manager', 'business operations'],
  },
  {
    key: 'hr',
    label: 'HR',
    labelAr: 'الموارد البشرية',
    technical: false,
    roles: ['HR Manager', 'People Operations Lead', 'Talent Acquisition Specialist', 'HR Business Partner'],
    skills: ['Recruitment', 'Employee Relations', 'Performance Management', 'HRIS', 'Compensation', 'Emiratization'],
    aliases: ['hr', 'human resources', 'people operations', 'talent acquisition', 'recruiter'],
  },
  {
    key: 'finance',
    label: 'Finance',
    labelAr: 'المالية',
    technical: false,
    roles: ['Finance Manager', 'Financial Analyst', 'Controller', 'FP&A Analyst'],
    skills: ['Budgeting', 'Forecasting', 'Reporting', 'Modeling', 'Accounts Payable', 'Accounts Receivable'],
    aliases: ['finance', 'financial analyst', 'fp&a', 'controller', 'accounting'],
  },
  {
    key: 'legal',
    label: 'Legal',
    labelAr: 'القانونية',
    technical: false,
    roles: ['Legal Counsel', 'Paralegal', 'Compliance Officer', 'Contract Specialist'],
    skills: ['Legal Research', 'Contract Drafting', 'Compliance', 'Corporate Law', 'Regulatory Affairs'],
    aliases: ['legal', 'compliance', 'contract specialist', 'paralegal', 'legal counsel'],
  },
  {
    key: 'healthcare',
    label: 'Healthcare',
    labelAr: 'الرعاية الصحية',
    technical: false,
    roles: ['Registered Nurse', 'Staff Nurse', 'Clinical Nurse', 'Patient Care Assistant'],
    skills: ['Patient Care', 'ICU Nursing', 'BLS', 'Medication Administration', 'Clinical Documentation'],
    aliases: ['healthcare', 'nursing', 'registered nurse', 'clinical nurse', 'medical'],
  },
  {
    key: 'education',
    label: 'Education',
    labelAr: 'التعليم',
    technical: false,
    roles: ['Teacher', 'Lecturer', 'Instructional Designer', 'Trainer'],
    skills: ['Curriculum Development', 'Classroom Management', 'Assessment', 'E-learning', 'Lesson Planning'],
    aliases: ['education', 'teacher', 'lecturer', 'trainer', 'instructional designer'],
  },
  {
    key: 'construction',
    label: 'Construction',
    labelAr: 'الإنشاءات',
    technical: false,
    roles: ['Site Engineer', 'Civil Engineer', 'Quantity Surveyor', 'Construction Manager'],
    skills: ['AutoCAD', 'Primavera', 'Quantity Surveying', 'Site Supervision', 'Project Scheduling'],
    aliases: ['construction', 'site engineer', 'civil engineer', 'quantity surveyor', 'primavera'],
  },
  {
    key: 'engineering',
    label: 'Engineering',
    labelAr: 'الهندسة',
    technical: false,
    roles: ['Mechanical Engineer', 'Electrical Engineer', 'Project Engineer', 'Maintenance Engineer'],
    skills: ['CAD', 'PLC', 'Project Engineering', 'Maintenance', 'Preventive Maintenance'],
    aliases: ['engineering', 'mechanical engineer', 'electrical engineer', 'project engineer'],
  },
  {
    key: 'manufacturing',
    label: 'Manufacturing',
    labelAr: 'التصنيع',
    technical: false,
    roles: ['Production Supervisor', 'Manufacturing Engineer', 'Plant Manager', 'Quality Supervisor'],
    skills: ['Lean Manufacturing', 'Quality Control', 'Production Planning', 'Safety', 'Operations Excellence'],
    aliases: ['manufacturing', 'production', 'plant manager', 'production supervisor', 'factory'],
  },
  {
    key: 'logistics',
    label: 'Logistics',
    labelAr: 'اللوجستيات',
    technical: false,
    roles: ['Logistics Coordinator', 'Warehouse Supervisor', 'Procurement Officer', 'Supply Chain Analyst'],
    skills: ['Warehouse Operations', 'Inventory Management', 'SAP', 'Procurement', 'Supply Chain Planning'],
    aliases: ['logistics', 'warehouse', 'procurement', 'supply chain', 'inventory'],
  },
  {
    key: 'hospitality',
    label: 'Hospitality',
    labelAr: 'الضيافة',
    technical: false,
    roles: ['Front Office Manager', 'Restaurant Manager', 'Guest Relations Officer', 'Hotel Supervisor'],
    skills: ['Guest Relations', 'Front Office', 'Food Safety', 'Reservation Systems', 'Service Standards'],
    aliases: ['hospitality', 'hotel', 'restaurant', 'front office', 'guest relations'],
  },
  {
    key: 'retail',
    label: 'Retail',
    labelAr: 'التجزئة',
    technical: false,
    roles: ['Store Manager', 'Retail Supervisor', 'Merchandiser', 'Sales Associate'],
    skills: ['Customer Service', 'POS', 'Merchandising', 'Inventory Control', 'Store Operations'],
    aliases: ['retail', 'store manager', 'merchandising', 'sales associate', 'shop'],
  },
  {
    key: 'government',
    label: 'Government',
    labelAr: 'الحكومة',
    technical: false,
    roles: ['Government Relations Officer', 'Public Sector Specialist', 'Administrative Officer'],
    skills: ['Government Relations', 'Policy', 'Documentation', 'Public Administration', 'Stakeholder Coordination'],
    aliases: ['government', 'public sector', 'government relations', 'municipality'],
  },
  {
    key: 'trades',
    label: 'Skilled Trades',
    labelAr: 'الحرف المهنية',
    technical: false,
    roles: ['HVAC Technician', 'Electrician', 'Maintenance Technician', 'Plumber'],
    skills: ['HVAC', 'Plumbing', 'Electrical Maintenance', 'Welding', 'Troubleshooting'],
    aliases: ['trades', 'hvac', 'electrician', 'maintenance technician', 'plumber'],
  },
  {
    key: 'support',
    label: 'Support',
    labelAr: 'الدعم',
    technical: false,
    roles: ['Customer Support Specialist', 'Technical Support Engineer', 'Support Analyst', 'Success Manager'],
    skills: ['Customer Success', 'Ticketing', 'Troubleshooting', 'Onboarding', 'Communication', 'SLA Management'],
    aliases: ['support', 'customer support', 'technical support', 'success manager'],
  },
];

const FAMILY_BY_KEY = new Map(
  CANONICAL_ROLE_FAMILIES.map((family) => [family.key, family]),
);

function normalizeText(value: string) {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value: string) {
  return normalizeText(value).replace(/\s+/g, '-');
}

function includesTerm(text: string, term: string) {
  const normalizedText = normalizeText(text);
  const normalizedTerm = normalizeText(term);

  if (!normalizedText || !normalizedTerm) {
    return false;
  }

  if (normalizedTerm.length <= 3) {
    return normalizedText.split(' ').includes(normalizedTerm);
  }

  return normalizedText.includes(normalizedTerm);
}

function getLocaleLabel(family: CanonicalRoleFamily, locale?: string | null) {
  return locale?.toLowerCase().startsWith('ar') ? family.labelAr : family.label;
}

function scoreFamilyText(text: string, family: CanonicalRoleFamily) {
  const normalized = normalizeText(text);
  if (!normalized) {
    return 0;
  }

  let score = 0;

  for (const alias of family.aliases) {
    if (includesTerm(normalized, alias)) {
      score += 3;
    }
  }

  for (const role of family.roles) {
    if (includesTerm(normalized, role)) {
      score += 4;
    }
  }

  for (const skill of family.skills) {
    if (includesTerm(normalized, skill)) {
      score += 1.5;
    }
  }

  return score;
}

function uniqueStrings(values: (string | null | undefined)[]) {
  return [...new Set(values.filter((value): value is string => Boolean(value && value.trim())))];
}

function collectProfileText(profile?: CVProfile | null) {
  if (!profile) {
    return '';
  }

  const skillTexts = profile.skillGroups.flatMap((group) => [
    group.category,
    ...group.items,
  ]);
  const preferenceTexts = [
    ...profile.jobPreferences.jobTitles,
    ...profile.jobPreferences.preferredLocations,
    ...profile.jobPreferences.locationTypes,
    ...profile.jobPreferences.employmentTypes,
  ];

  return [
    profile.headline,
    profile.summary,
    profile.location,
    ...skillTexts,
    ...preferenceTexts,
  ]
    .filter(Boolean)
    .join(' ');
}

function getJobDescription(job: CatalogJobTaxonomyInput) {
  return 'description' in job && typeof job.description === 'string' ? job.description : '';
}

function getJobRequirements(job: CatalogJobTaxonomyInput) {
  return 'requirements' in job && Array.isArray(job.requirements) ? job.requirements : [];
}

function getJobTags(job: CatalogJobTaxonomyInput) {
  return 'tags' in job && Array.isArray(job.tags) ? job.tags : [];
}

function getJobNormalizedSkills(job: CatalogJobTaxonomyInput) {
  return 'normalizedSkills' in job && Array.isArray(job.normalizedSkills)
    ? job.normalizedSkills
    : [];
}

function collectJobText(job: CatalogJobTaxonomyInput) {
  const description = getJobDescription(job);
  const requirements = getJobRequirements(job);
  const tags = getJobTags(job);
  const normalizedSkills = getJobNormalizedSkills(job);

  return [
    job.title,
    job.summary ?? '',
    description,
    job.location ?? '',
    job.city ?? '',
    job.country ?? '',
    job.type ?? '',
    job.workMode ?? '',
    job.level ?? '',
    ...normalizedSkills,
    ...tags,
    ...requirements,
    job.company.name,
  ]
    .filter(Boolean)
    .join(' ');
}

function calculateContentCompleteness(job: CatalogJobTaxonomyInput) {
  const description = getJobDescription(job);
  const requirements = getJobRequirements(job);
  const tags = getJobTags(job);
  const normalizedSkills = getJobNormalizedSkills(job);

  const checks = [
    Boolean((job.summary ?? '').trim() || description.trim()),
    Boolean(normalizedSkills.length || requirements.length),
    Boolean(tags.length),
    Boolean(job.applicationUrl),
    Boolean(job.location || job.city || job.country),
    Boolean(job.postedAt),
  ];

  const base = checks.filter(Boolean).length / checks.length;
  const descriptionBonus =
    Math.min(((job.summary ?? '').trim() || description.trim()).length, 240) / 240 * 0.2;
  const requirementsBonus =
    Math.min(Math.max(normalizedSkills.length, requirements.length), 6) / 6 * 0.2;
  const tagBonus = Math.min(tags.length, 6) / 6 * 0.1;

  return Math.min(1, base * 0.5 + descriptionBonus + requirementsBonus + tagBonus);
}

function inferFamilyForText(text: string) {
  let bestFamily: CanonicalRoleFamily | null = null;
  let bestScore = 0;

  for (const family of CANONICAL_ROLE_FAMILIES) {
    const score = scoreFamilyText(text, family);
    if (score > bestScore) {
      bestScore = score;
      bestFamily = family;
    }
  }

  return { family: bestFamily, score: bestScore };
}

export function getCanonicalFamilyByKey(key?: string | null) {
  if (!key) {
    return null;
  }

  const existing = FAMILY_BY_KEY.get(key as CanonicalRoleFamilyKey);
  if (existing) {
    return existing;
  }

  const label = key
    .split(/[-_]/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

  return {
    key,
    label,
    labelAr: label,
    technical: false,
    roles: [label],
    skills: [],
    aliases: [key],
  } satisfies CanonicalRoleFamily;
}

export function getCanonicalFamilyLabel(
  key: CanonicalRoleFamilyKey | string,
  locale?: string | null,
) {
  const family = getCanonicalFamilyByKey(key);
  if (!family) {
    return key;
  }

  return getLocaleLabel(family, locale);
}

export function getCanonicalSkillsForFamily(key: CanonicalRoleFamilyKey, limit = FAMILY_SKILL_LIMIT) {
  const family = FAMILY_BY_KEY.get(key);
  if (!family) {
    return [];
  }

  return family.skills.slice(0, limit);
}

export function getCanonicalRolesForFamily(key: CanonicalRoleFamilyKey, limit = 4) {
  const family = FAMILY_BY_KEY.get(key);
  if (!family) {
    return [];
  }

  return family.roles.slice(0, limit);
}

export function getSuggestedFamiliesFromProfile(
  profile?: CVProfile | null,
  locale?: string | null,
) {
  const familyScores = new Map<CanonicalRoleFamilyKey, number>();
  const profileText = collectProfileText(profile);

  for (const family of CANONICAL_ROLE_FAMILIES) {
    const score = scoreFamilyText(profileText, family);
    if (score > 0) {
      familyScores.set(family.key, score);
    }
  }

  const ranked = [...familyScores.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5)
    .map(([key, score]) => {
      const family = getCanonicalFamilyByKey(key);
      if (!family) {
        return null;
      }

      return {
        key: family.key,
        label: getLocaleLabel(family, locale),
        labelAr: family.labelAr,
        technical: family.technical,
        score,
        skills: family.skills.slice(0, FAMILY_SKILL_LIMIT),
        roles: family.roles.slice(0, 4),
      };
    })
    .filter(
      (entry): entry is NonNullable<typeof entry> => Boolean(entry),
    );

  if (ranked.length > 0) {
    return ranked;
  }

  return CANONICAL_ROLE_FAMILIES.slice(0, 4).map((family) => ({
    key: family.key,
    label: getLocaleLabel(family, locale),
    labelAr: family.labelAr,
    technical: family.technical,
    score: 0,
    skills: family.skills.slice(0, FAMILY_SKILL_LIMIT),
    roles: family.roles.slice(0, 4),
  }));
}

function computeProfileTaxonomySignals(profile?: CVProfile | null): ProfileTaxonomySignals {
  const suggestedFamilies = getSuggestedFamiliesFromProfile(profile);
  const primaryFamily = getCanonicalFamilyByKey(suggestedFamilies[0]?.key ?? null);
  const skillHints = uniqueStrings(
    profile
      ? profile.skillGroups.flatMap((group) => group.items)
      : [],
  ).slice(0, 8);
  const roleHints = uniqueStrings(
    profile?.jobPreferences.jobTitles ?? [],
  ).slice(0, 6);

  const query = uniqueStrings([
    primaryFamily?.label,
    ...(primaryFamily?.skills.slice(0, 3) ?? []),
    ...skillHints.slice(0, 3),
    ...roleHints.slice(0, 2),
  ]).join(' ');

  return {
    familyKeys: suggestedFamilies.map((family) => family.key),
    primaryFamily,
    skillHints,
    roleHints,
    query,
  };
}

// ─── Memoisation caches ──────────────────────────────────────────────
// `getCatalogJobFit` / `buildProfileTaxonomySignals` are pure functions of their
// (job, profile) inputs, but they re-scan all role families and re-normalise text
// on every call: heavy enough to block the Jobs tab switch (1-2s). React Query
// hands us stable object references for jobs and profiles until a refetch replaces
// them, so we memoise by reference with WeakMaps (auto-evicted on GC, no manual
// invalidation). A content change yields a new reference → cache miss → recompute.
const signalsCache = new WeakMap<object, ProfileTaxonomySignals>();
const NULL_PROFILE_SIGNALS = computeProfileTaxonomySignals(null);

/** Reference-cached profile signals. Identical `profile` ref → same signals object. */
export function buildProfileTaxonomySignals(profile?: CVProfile | null): ProfileTaxonomySignals {
  if (!profile) return NULL_PROFILE_SIGNALS;
  const cached = signalsCache.get(profile);
  if (cached) return cached;
  const computed = computeProfileTaxonomySignals(profile);
  signalsCache.set(profile, computed);
  return computed;
}

// job ref → (signals ref → fit). The fit depends on `profile` only through its
// signals, so the inner key is the signals object (one per profile reference).
const fitCache = new WeakMap<object, WeakMap<ProfileTaxonomySignals, CatalogJobFitSummary>>();

export function searchCanonicalSkills(query: string, limit = 24) {
  const normalizedQuery = normalizeText(query);
  const options = CANONICAL_ROLE_FAMILIES.flatMap((family) =>
    family.skills.map((skill) => ({
      id: `skill:${slugify(skill)}`,
      key: slugify(skill),
      familyKey: family.key,
      familyLabel: family.label,
      familyLabelAr: family.labelAr,
      label: skill,
      labelAr: skill,
      aliases: uniqueStrings([skill, family.label, ...family.aliases, ...family.roles]),
      kind: 'skill' as const,
    })),
  );

  const filtered = normalizedQuery
    ? options.filter((option) =>
        includesTerm(option.label, normalizedQuery) ||
        option.aliases.some((alias) => includesTerm(alias, normalizedQuery)),
      )
    : options;

  return filtered.slice(0, limit);
}

export function searchCanonicalRoles(query: string, limit = 24) {
  const normalizedQuery = normalizeText(query);
  const options = CANONICAL_ROLE_FAMILIES.flatMap((family) =>
    family.roles.map((role) => ({
      id: `role:${slugify(role)}`,
      key: slugify(role),
      familyKey: family.key,
      familyLabel: family.label,
      familyLabelAr: family.labelAr,
      label: role,
      labelAr: role,
      aliases: uniqueStrings([role, family.label, ...family.aliases, ...family.skills]),
      kind: 'role' as const,
    })),
  );

  const filtered = normalizedQuery
    ? options.filter((option) =>
        includesTerm(option.label, normalizedQuery) ||
        option.aliases.some((alias) => includesTerm(alias, normalizedQuery)),
      )
    : options;

  return filtered.slice(0, limit);
}

/** Reference-cached job fit. Same (job, profile) refs → same fit object. */
export function getCatalogJobFit(
  job: CatalogJobTaxonomyInput,
  profile?: CVProfile | null,
): CatalogJobFitSummary {
  const signals = buildProfileTaxonomySignals(profile);
  let perJob = fitCache.get(job);
  if (!perJob) {
    perJob = new WeakMap();
    fitCache.set(job, perJob);
  }
  const cached = perJob.get(signals);
  if (cached) return cached;
  const fit = computeCatalogJobFit(job, signals);
  perJob.set(signals, fit);
  return fit;
}

function computeCatalogJobFit(
  job: CatalogJobTaxonomyInput,
  profileSignals: ProfileTaxonomySignals,
): CatalogJobFitSummary {
  const jobText = collectJobText(job);
  const explicitFamily = getCanonicalFamilyByKey(
    typeof job.roleKey === 'string' ? job.roleKey : null,
  );
  const inferred = explicitFamily ?? inferFamilyForText(jobText).family;
  const matchedSkills = uniqueStrings([
    ...(job.matchedSkills ?? []),
    ...(profileSignals.skillHints.filter((skill) => includesTerm(jobText, skill))),
    ...(inferred?.skills.filter((skill) => includesTerm(jobText, skill)) ?? []),
  ]);
  const missingSkills = profileSignals.skillHints.filter(
    (skill) => !matchedSkills.some((match) => normalizeText(match) === normalizeText(skill)),
  );
  const contentCompleteness = job.contentCompleteness ?? calculateContentCompleteness(job);
  const roleConfidence =
    job.roleConfidence ??
    (inferred
      ? Math.min(0.95, 0.45 + scoreFamilyText(jobText, inferred) / 12)
      : matchedSkills.length > 0
        ? 0.55
        : 0.3);
  const roleKey = explicitFamily?.key ?? inferred?.key ?? null;
  const roleFamily = inferred ? inferred.label : job.roleFamily ?? null;
  const surfaceEligible =
    job.surfaceEligible ??
    (contentCompleteness >= 0.45 && (matchedSkills.length > 0 || (roleConfidence ?? 0) >= 0.5));
  const description = getJobDescription(job);
  const requirements = getJobRequirements(job);
  const normalizedSkills = getJobNormalizedSkills(job);

  const qualityFlags = uniqueStrings([
    !((job.summary ?? '').trim() || description.trim()) ? 'no_description' : null,
    !normalizedSkills.length && !requirements.length ? 'thin_requirements' : null,
    contentCompleteness < 0.6 ? 'low_completeness' : null,
    matchedSkills.length === 0 ? 'no_skill_overlap' : null,
    (roleConfidence ?? 0) < 0.65 ? 'low_confidence' : null,
    surfaceEligible ? null : 'surface_ineligible',
  ]);

  return {
    roleFamily,
    roleKey,
    roleConfidence,
    matchedSkills,
    missingSkills: missingSkills.slice(0, 5),
    contentCompleteness,
    qualityFlags,
    surfaceEligible,
  };
}

export function shouldSurfaceInDiscover(
  job: CatalogJobTaxonomyInput,
  profile?: CVProfile | null,
) {
  const fit = getCatalogJobFit(job, profile);
  const primaryFamily = buildProfileTaxonomySignals(profile).primaryFamily;
  const isTechnical = fit.roleKey ? TECH_FAMILIES.has(fit.roleKey as CanonicalRoleFamilyKey) : false;

  if (!fit.surfaceEligible) {
    return false;
  }

  if (!fit.roleKey) {
    return fit.matchedSkills.length > 0;
  }

  if (primaryFamily?.key === fit.roleKey) {
    return (fit.roleConfidence ?? 0) >= 0.55 && (fit.matchedSkills.length > 0 || !isTechnical);
  }

  if (primaryFamily && TECH_FAMILIES.has(primaryFamily.key) && isTechnical) {
    return (
      fit.matchedSkills.length > 0 ||
      (fit.roleConfidence ?? 0) >= 0.72 ||
      (fit.contentCompleteness ?? 0) >= 0.75
    );
  }

  if (!isTechnical) {
    return (fit.roleConfidence ?? 0) >= 0.65 || fit.matchedSkills.length > 0;
  }

  return fit.matchedSkills.length > 0 && (fit.roleConfidence ?? 0) >= 0.6;
}

export function filterDiscoverableJobs(
  jobs: CatalogJobTaxonomyInput[],
  profile?: CVProfile | null,
) {
  return jobs.filter((job) => shouldSurfaceInDiscover(job, profile));
}

export function buildDiscoverSearchQuery(profile?: CVProfile | null) {
  const signals = buildProfileTaxonomySignals(profile);
  const queryTerms = uniqueStrings([
    ...signals.skillHints.slice(0, 3),
    ...signals.roleHints.slice(0, 2),
    signals.primaryFamily?.label,
    ...(signals.primaryFamily?.skills.slice(0, 2) ?? []),
  ]);

  return queryTerms.join(' ');
}

function uniqueRelatedTerms(values: (string | null | undefined)[]) {
  const seen = new Set<string>();
  const terms: string[] = [];

  for (const value of values) {
    const normalizedValue = value
      ?.replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalizedValue || normalizedValue.length < 2 || normalizedValue.length > 48) {
      continue;
    }

    const key = normalizeText(normalizedValue);
    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    terms.push(normalizedValue);
  }

  return terms;
}

export function buildRelatedCatalogJobsQuery(
  job: CatalogJobTaxonomyInput,
  profile?: CVProfile | null,
) {
  const fit = getCatalogJobFit(job, profile);
  const family = getCanonicalFamilyByKey(fit.roleKey);
  const profileSignals = buildProfileTaxonomySignals(profile);
  const normalizedSkills = getJobNormalizedSkills(job);
  const jobTags = getJobTags(job);
  const skillTerms = uniqueRelatedTerms([
    ...fit.matchedSkills,
    ...normalizedSkills,
    ...jobTags,
  ]);
  const profileSkillOverlap = profileSignals.skillHints.filter((skill) =>
    skillTerms.some((term) => includesTerm(term, skill) || includesTerm(skill, term)),
  );

  const terms = uniqueRelatedTerms([
    family?.label,
    ...(family?.roles.slice(0, 2) ?? []),
    job.roleFamily,
    job.title,
    ...profileSkillOverlap.slice(0, 2),
    ...skillTerms.slice(0, 4),
    job.type,
    job.level,
  ]);

  return terms.slice(0, 8).join(' ');
}

export function inferRoleFamilyFromText(text: string) {
  const { family, score } = inferFamilyForText(text);
  return {
    family,
    score,
  };
}

export function inferRoleFamilyFromSkillGroups(groups: CVSkillGroup[]) {
  const familyScores = new Map<CanonicalRoleFamilyKey, number>();
  for (const group of groups) {
    const text = [group.category, ...group.items].filter(Boolean).join(' ');
    const result = inferRoleFamilyFromText(text);
    if (result.family) {
      familyScores.set(result.family.key, (familyScores.get(result.family.key) ?? 0) + result.score);
    }
  }

  return [...familyScores.entries()]
    .sort((left, right) => right[1] - left[1])
    .map(([key, score]) => ({
      family: getCanonicalFamilyByKey(key),
      score,
    }))
    .filter((entry): entry is { family: CanonicalRoleFamily; score: number } => Boolean(entry.family));
}
